import { z } from "zod";
import { type Contenido, esquemaIdDeBloque, type Nodo, nodosDeTipo, textoDe } from "./contenido.js";
import { sinTildes } from "./texto.js";

/**
 * El personal del día de una hoja llena (pedido de Xavier del 2026-10-28). La hoja de higiene
 * (GES-REG-001) lleva una fila por persona inspeccionada y todos los días son casi los mismos
 * nombres y áreas: en vez de escribirlos a mano cada vez, quien la llena guarda su lista, al abrir
 * la hoja desmarca a quien faltó y la app escribe nombre y área en las filas, sin huecos. La
 * plantilla no cambia: sigue en blanco hasta que la persona lo pide.
 *
 * Lo escrito a máquina va en `hojas.campos` (id de la celda → texto); `aplicarCampos` lo mete en
 * el contenido del formato antes de dibujar la hoja (pantalla y PDF), así cae en la misma celda
 * en las dos. La lista se guarda por persona y formato (`personal_de_hojas`), no en el formato:
 * la de Emily no es la de otro supervisor.
 */

/**
 * Las áreas de la planta donde trabaja el personal que se inspecciona (Xavier, 2026-10-28): son
 * las que van en la columna "Área" de la hoja de higiene. El panel las ofrece al escribir el área
 * de una persona; se puede escribir otra.
 */
export const AREAS_DE_PLANTA = ["Almacén", "Líquidos", "Sólidos", "Logística"] as const;

export const esquemaPersonaDeHoja = z.object({
  nombre: z.string().trim().min(1).max(80),
  area: z.string().trim().max(60).default(""),
});
type PersonaDeHoja = z.infer<typeof esquemaPersonaDeHoja>;

/** Cuántas personas caben en la lista guardada de una persona para un formato. */
export const MAXIMO_PERSONAL_POR_HOJA = 80;
export type PersonalDeHoja = PersonaDeHoja[];

/** Celda del formato → lo que la app escribió en ella. */
export const esquemaCampos = z.record(esquemaIdDeBloque, z.string().max(200));
export type Campos = Record<string, string>;

/** La tabla del formato con una columna "Nombre" (y, si la hay, "Área"): dónde va cada persona. */
type TablaDePersonal = {
  /** Las filas libres, en orden: los ids de la celda del nombre y de la del área. */
  filas: { nombre: string; area: string | null }[];
};

/**
 * Busca en el formato la primera tabla cuyo encabezado tenga una columna "Nombre" (y opcionalmente
 * "Área"), y devuelve las filas donde la celda del nombre está vacía: ahí se escribe el personal.
 * Null si el formato no tiene una tabla así (no todo formato lista personas).
 */
export function tablaDePersonal(contenido: Contenido): TablaDePersonal | null {
  for (const tabla of nodosDeTipo(contenido, "table")) {
    const [encabezado, ...filas] = tabla.content ?? [];
    if (!encabezado?.content) continue;
    const columnas = columnasDe(encabezado);
    const nombre = columnas.find((c) => /^nombres?\b/.test(normalizar(textoDe(c.celda))));
    if (!nombre) continue;
    const area = columnas.find((c) => /^areas?\b/.test(normalizar(textoDe(c.celda))));
    const libres: TablaDePersonal["filas"] = [];
    for (const fila of filas) {
      const celdas = columnasDe(fila);
      const celdaNombre = celdas.find((c) => c.desde === nombre.desde)?.celda;
      if (!celdaNombre || !estaVacia(celdaNombre)) continue;
      const celdaArea = area ? celdas.find((c) => c.desde === area.desde)?.celda : undefined;
      libres.push({
        nombre: idDe(celdaNombre),
        area: celdaArea && estaVacia(celdaArea) ? idDe(celdaArea) : null,
      });
    }
    if (libres.length) return { filas: libres };
  }
  return null;
}

/**
 * Los campos para poner a estas personas en la tabla, una por fila y sin huecos. Quien llama
 * comprueba antes que quepan (`presentes.length <= tabla.filas.length`); las filas de más quedan
 * sin campo, es decir, en blanco.
 */
export function camposDePersonal(tabla: TablaDePersonal, presentes: PersonalDeHoja): Campos {
  const campos: Campos = {};
  presentes.forEach((p, i) => {
    const fila = tabla.filas[i];
    if (!fila) return;
    campos[fila.nombre] = p.nombre;
    if (fila.area && p.area) campos[fila.area] = p.area;
  });
  return campos;
}

/** Los campos de la hoja que no son de la tabla de personal: se conservan al volver a ponerlo. */
export function camposAjenos(tabla: TablaDePersonal, campos: Campos): Campos {
  const deLaTabla = new Set(tabla.filas.flatMap((f) => (f.area ? [f.nombre, f.area] : [f.nombre])));
  return Object.fromEntries(Object.entries(campos).filter(([id]) => !deLaTabla.has(id)));
}

/** Quiénes están puestos en la hoja, leídos de sus campos. */
export function personalPuesto(tabla: TablaDePersonal, campos: Campos): PersonalDeHoja {
  const puestos: PersonalDeHoja = [];
  for (const fila of tabla.filas) {
    const nombre = campos[fila.nombre]?.trim();
    if (!nombre) continue;
    puestos.push({ nombre, area: (fila.area && campos[fila.area]?.trim()) || "" });
  }
  return puestos;
}

/**
 * El contenido del formato con lo escrito a máquina dentro de sus celdas: el texto va en el
 * primer párrafo de la celda, que en los formatos del seed es el renglón en blanco. No cambia el
 * contenido que se recibe (ese es el de la revisión, con su hash).
 */
export function aplicarCampos(contenido: Contenido, campos: Campos): Contenido {
  if (Object.keys(campos).length === 0) return contenido;
  const copia = structuredClone(contenido);
  for (const celda of nodosDeTipo(copia, "tableCell")) {
    const texto = campos[idDe(celda)];
    if (!texto || !celda.content?.[0]) continue;
    const parrafo = celda.content[0];
    parrafo.content = [{ type: "text", text: texto }];
  }
  return copia;
}

/** Una celda de datos (no de encabezado) sin nada escrito: donde se puede poner a alguien. */
function estaVacia(celda: Nodo): boolean {
  return celda.type === "tableCell" && textoDe(celda).trim() === "";
}

/** Las celdas de una fila con la columna en que empieza cada una (contando los `colspan`). */
function columnasDe(fila: Nodo): { celda: Nodo; desde: number }[] {
  let desde = 0;
  return (fila.content ?? []).map((celda) => {
    const columna = { celda, desde };
    desde += Number(celda.attrs?.colspan ?? 1) || 1;
    return columna;
  });
}

function idDe(nodo: Nodo): string {
  return String(nodo.attrs?.id ?? "");
}

/** Sin tildes ni mayúsculas, para comparar encabezados: "Área" y "AREA" son lo mismo. */
function normalizar(texto: string): string {
  return sinTildes(texto).trim().toLowerCase();
}
